// src/gestion-logistics/components/ContainerManager.tsx
import React, { useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Space,
  Popconfirm,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  Container,
  ContainerSizeFeet,
  ContainerHeightType,
  ContainerStatus,
} from "../types/shipment.types";

const { Option } = Select;

interface ContainerManagerProps {
  containers: Container[];
  onChange: (containers: Container[]) => void;
  disabled?: boolean;
}

/**
 * Composant de gestion des conteneurs pour les dossiers maritimes
 * Tableau éditable en inline avec CRUD complet
 */
const ContainerManager: React.FC<ContainerManagerProps> = ({
  containers = [],
  onChange,
  disabled = false,
}) => {
  const [editingKey, setEditingKey] = useState<number | "new" | null>(null);
  const [form] = Form.useForm();

  const isEditing = (index: number | "new") => editingKey === index;

  // Ajouter un nouveau conteneur
  const handleAdd = () => {
    if (editingKey !== null) {
      message.warning("Veuillez terminer l'édition en cours");
      return;
    }

    const newContainer: Container = {
      container_number: "",
      container_type: "",
      seal_number: "",
      weight: undefined,
      volume: undefined,
      size_feet: ContainerSizeFeet.FORTY,
      height_type: ContainerHeightType.STANDARD,
      is_reefer: false,
      status: ContainerStatus.LOADED,
    };

    onChange([...containers, newContainer]);
    setEditingKey(containers.length);
    form.setFieldsValue(newContainer);
  };

  // Éditer un conteneur existant
  const handleEdit = (index: number) => {
    form.setFieldsValue(containers[index]);
    setEditingKey(index);
  };

  // Sauvegarder les modifications
  const handleSave = async (index: number) => {
    try {
      const values = await form.validateFields();
      const newContainers = [...containers];
      newContainers[index] = { ...newContainers[index], ...values };
      onChange(newContainers);
      setEditingKey(null);
      form.resetFields();
      message.success("Conteneur sauvegardé");
    } catch (error) {
      console.error("[CONTAINER_MANAGER] Validation failed:", error);
    }
  };

  // Annuler l'édition
  const handleCancel = (index: number) => {
    // Si c'est un nouveau conteneur vide, le supprimer
    if (
      index === containers.length - 1 &&
      !containers[index].container_number
    ) {
      onChange(containers.slice(0, -1));
    }
    setEditingKey(null);
    form.resetFields();
  };

  // Supprimer un conteneur
  const handleDelete = (index: number) => {
    const newContainers = containers.filter((_, i) => i !== index);
    onChange(newContainers);
    message.success("Conteneur supprimé");
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "N° Conteneur",
      dataIndex: "container_number",
      key: "container_number",
      width: 180,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item
            name="container_number"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: "N° conteneur requis" },
              { max: 50, message: "Maximum 50 caractères" },
            ]}
          >
            <Input placeholder="Ex: CSQU3054383" />
          </Form.Item>
        ) : (
          <span>{record.container_number || "-"}</span>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "size_feet",
      key: "size_feet",
      width: 100,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="size_feet" style={{ margin: 0 }}>
            <Select>
              <Option value={ContainerSizeFeet.TWENTY}>20'</Option>
              <Option value={ContainerSizeFeet.FORTY}>40'</Option>
              <Option value={ContainerSizeFeet.FORTYFIVE}>45'</Option>
            </Select>
          </Form.Item>
        ) : (
          <span>{record.size_feet ? `${record.size_feet}'` : "-"}</span>
        );
      },
    },
    {
      title: "HC",
      dataIndex: "height_type",
      key: "height_type",
      width: 80,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="height_type" style={{ margin: 0 }}>
            <Select>
              <Option value={ContainerHeightType.STANDARD}>Standard</Option>
              <Option value={ContainerHeightType.HIGH_CUBE}>HC</Option>
            </Select>
          </Form.Item>
        ) : (
          <span>
            {record.height_type === ContainerHeightType.HIGH_CUBE
              ? "HC"
              : "STD"}
          </span>
        );
      },
    },
    {
      title: "N° Seal",
      dataIndex: "seal_number",
      key: "seal_number",
      width: 150,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="seal_number" style={{ margin: 0 }}>
            <Input placeholder="Numéro de scellé" />
          </Form.Item>
        ) : (
          <span>{record.seal_number || "-"}</span>
        );
      },
    },
    {
      title: "Poids (kg)",
      dataIndex: "weight",
      key: "weight",
      width: 120,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="weight" style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              placeholder="0.00"
            />
          </Form.Item>
        ) : (
          <span>{record.weight || "-"}</span>
        );
      },
    },
    {
      title: "Volume (m³)",
      dataIndex: "volume",
      key: "volume",
      width: 120,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="volume" style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.001}
              placeholder="0.000"
            />
          </Form.Item>
        ) : (
          <span>{record.volume || "-"}</span>
        );
      },
    },
    {
      title: "Reefer",
      dataIndex: "is_reefer",
      key: "is_reefer",
      width: 80,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item
            name="is_reefer"
            valuePropName="checked"
            style={{ margin: 0 }}
          >
            <Checkbox />
          </Form.Item>
        ) : (
          <span>{record.is_reefer ? "Oui" : "Non"}</span>
        );
      },
    },
    {
      title: "Temp. (°C)",
      dataIndex: "temperature",
      key: "temperature",
      width: 100,
      render: (_: any, record: Container, index: number) => {
        return isEditing(index) ? (
          <Form.Item name="temperature" style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={-30}
              max={30}
              placeholder="-18"
            />
          </Form.Item>
        ) : (
          <span>{record.temperature !== undefined ? record.temperature : "-"}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: Container, index: number) => {
        const editing = isEditing(index);
        return editing ? (
          <Space size="small">
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => handleSave(index)}
              size="small"
            >
              Sauver
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={() => handleCancel(index)}
              size="small"
            >
              Annuler
            </Button>
          </Space>
        ) : (
          <Space size="small">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(index)}
              disabled={editingKey !== null || disabled}
              size="small"
            />
            <Popconfirm
              title="Supprimer ce conteneur ?"
              onConfirm={() => handleDelete(index)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey !== null || disabled}
                size="small"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="dashed"
          onClick={handleAdd}
          icon={<PlusOutlined />}
          disabled={editingKey !== null || disabled}
          block
        >
          Ajouter un conteneur
        </Button>
      </div>

      <Form form={form} component={false}>
        <Table
          dataSource={containers}
          columns={columns}
          rowKey={(record, index) => `container-${index}`}
          pagination={false}
          scroll={{ x: 1200 }}
          size="small"
          bordered
          locale={{
            emptyText: "Aucun conteneur. Cliquez sur 'Ajouter' pour commencer.",
          }}
        />
      </Form>

      {containers.length > 0 && (
        <div style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
          {containers.length} conteneur(s)
        </div>
      )}
    </div>
  );
};

export default ContainerManager;
